import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectApplicationInfo } from 'legacyfrontend/redux/selectors/application-selectors';

const COUNTRY_CONFIGS = {
  1: {
    // Argentina
    country: 'Argentina',
    currency: 'ARS',
    currencySymbol: '$',
    phonePrefix: '+54',
    languages: ['es', 'en'],
    defaultLanguage: 'es',
    labels: {
      registration: 'Legajo',
      taxId: 'CUIT',
    },
    legalRequirements: {
      showLegajo: true,
      showCUIT: true,
    },
    consumerRightsText: 'Botón de arrepentimiento',
    consumerRightsLaw: 'Ley 24.240',
    showGeneralDirection: true,
  },
  2: {
    // Perú
    country: 'Perú',
    currency: 'PEN',
    currencySymbol: 'S/',
    phonePrefix: '+51',
    languages: ['es', 'en'],
    defaultLanguage: 'es',
    labels: {
      registration: 'RUC',
      taxId: 'RUC',
    },
    legalRequirements: {
      showRUC: true,
    },
    showGeneralDirection: false,
  },
  3: {
    // Brasil
    country: 'Brasil',
    currency: 'BRL',
    currencySymbol: 'R$',
    phonePrefix: '+55',
    languages: ['pt', 'en'],
    defaultLanguage: 'pt',
    labels: {
      registration: 'CADASTUR',
      taxId: 'CNPJ',
    },
    legalRequirements: {
      showCADASTUR: true,
      showCNPJ: true,
    },
    showGeneralDirection: false,
  },
  4: {
    // México
    country: 'México',
    currency: 'MXN',
    currencySymbol: '$',
    phonePrefix: '+52',
    languages: ['es', 'en'],
    defaultLanguage: 'es',
    labels: {
      registration: '',
      taxId: '',
    },
    legalRequirements: {},
    showGeneralDirection: false,
  },
};

export const useTemplateConfig = () => {
  const { i18n, t } = useTranslation(['layout']);
  const applicationInfo = useSelector(selectApplicationInfo);
  const paisId = applicationInfo?.paisId;

  const config = useMemo(() => {
    return COUNTRY_CONFIGS[paisId] || COUNTRY_CONFIGS[1]; // Default a Argentina
  }, [paisId]);

  useEffect(() => {
    if (
      config.defaultLanguage &&
      i18n.language !== config.defaultLanguage &&
      config.languages.includes(config.defaultLanguage) &&
      paisId
    ) {
      i18n.changeLanguage(config.defaultLanguage);
    }
  }, [config.defaultLanguage, paisId]);

  const formatters = useMemo(
    () => ({
      phone: (phone) => {
        if (!phone) return '';
        if (phone.startsWith('+')) return phone;
        return `${config.phonePrefix} ${phone}`;
      },
    }),
    [config.phonePrefix]
  );

  const showConsumerRightsSection = useMemo(() => {
    return paisId === 1;
  }, [paisId]);

  const translatedTexts = useMemo(
    () => ({
      payMethods: t('footer.pay_methods'),
      generalConditions: t('footer.generalConditions'),
      useTerms: t('footer.useTerms'),
      privacyPolicy: t('footer.privacyPolicy'),
      claimsBook: t('footer.claimsBook'),
    }),
    [t]
  );

  const templateConfig = useMemo(() => {
    const disabledLinks = applicationInfo?.templateData?.disabledLinks || [];

    const isLinkDisabled = (linkNameOrNames) => {
      const names = Array.isArray(linkNameOrNames) ? linkNameOrNames : [linkNameOrNames];
      return disabledLinks.some((disabledLink) => {
        const raw = disabledLink.defaultEnlace?.nombre;
        if (raw == null) return false;
        const backendLinkName = raw.normalize('NFD').toLowerCase();
        return names.some(
          (name) => name != null && name.normalize('NFD').toLowerCase() === backendLinkName
        );
      });
    };

    let showTermsOfUse = false;
    let showPrivacyPolicy = false;
    let showPaymentMethods = false;
    let showGeneralContractingConditions = false;
    let showClaimsBookLink = false;

    if (paisId === 1) {
      showPaymentMethods = !isLinkDisabled('Formas de pago');
      showGeneralContractingConditions = !isLinkDisabled('Condiciones generales de Contratación');
    } else if (paisId === 2) {
      // Perú
      showTermsOfUse = !isLinkDisabled('Términos y Condiciones de Uso');
      showPrivacyPolicy = !isLinkDisabled('Política de Privacidad');
      showPaymentMethods = !isLinkDisabled('Formas de pago');
      showClaimsBookLink = true; // SIEMPRE activo para Perú
    } else if (paisId === 3) {
      // Brasil
      showTermsOfUse = !isLinkDisabled([
        'Termos de Uso Site respectivamente',
        'Termos de Uso Site respetivamente',
      ]);
      showPrivacyPolicy = !isLinkDisabled('Política de Privacidade');
      showPaymentMethods = !isLinkDisabled('Formas de pago');
      showGeneralContractingConditions = !isLinkDisabled('Condições gerais de contratação');
    } else if (paisId === 4) {
      // México
      showPrivacyPolicy = !isLinkDisabled(['Políticas de Privacidad', 'Política de Privacidad']);
      showGeneralContractingConditions = !isLinkDisabled([
        'Condiciones generales de Contratación',
      ]);
    }

    return {
      id: paisId,
      showConsumerRights: showConsumerRightsSection,
      showPaymentMethods,
      showGeneralContractingConditions,
      showTermsOfUse,
      showPrivacyPolicy,
      showClaimsBook: showClaimsBookLink,
    };
  }, [
    paisId,
    showConsumerRightsSection,
    translatedTexts,
    applicationInfo?.templateData?.disabledLinks,
  ]);

  return {
    paisId,
    config,
    formatters,
    country: config.country,
    languages: config.languages,
    defaultLanguage: config.defaultLanguage,
    labels: config.labels,
    legal: config.legalRequirements,
    consumerRightsText: config.consumerRightsText || null,
    claimsBookText: templateConfig.showClaimsBook ? translatedTexts.claimsBook : null,
    showConsumerRightsSection,
    consumerRightsLaw: config.consumerRightsLaw || null,
    showGeneralDirection: config.showGeneralDirection,
    showClaimsBook: templateConfig.showClaimsBook,
    templateConfig,
  };
};
